import React from 'react';
import assistantClient from '@requests/telnyx/assistants.requests';

import { Modal, Button, LoadingButton } from '@common/index';
import { Stack } from '@mui/material';
import { useTheme } from '../../../styles/theme';

import { useDispatch, useSelector } from 'react-redux';
import { flowActions } from '@store/slices';
import { AppDispatch, State } from '@store/index';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DeleteOutlined } from '@mui/icons-material';
import { AIAssistant, AIAssistantDelete } from '@requests/telnyx';
import { RequestError } from '@requests/common';

export const DeleteAssitant: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const { enqueueSnackbar } = useSnackbar();

  const assistantId = useSelector((state: State) => state.flow.deleteAssistant);
  const setOpen = (val: string | null) => dispatch(flowActions.setDeleteAssistant(val));

  const { data: assistants = [] } = useQuery<AIAssistant[], RequestError>({
    queryKey: ['ai_assistants'],
    queryFn: assistantClient.list,
    retry: false,
  });

  const assistant = assistants.find((assistant) => assistant.id === assistantId);

  const { mutateAsync: remove, isPending: isDeleteLoading } = useMutation<void, RequestError, AIAssistantDelete>({
    mutationFn: assistantClient.remove,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai_assistants'] });
      enqueueSnackbar('Assistant deleted successfully!', { variant: 'success', autoHideDuration: 3000 });
      setOpen(null);
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar('Failed to delete assistant!', {
        variant: 'error',
        autoHideDuration: 10000,
      });

      enqueueSnackbar(error.message, {
        variant: 'error',
        autoHideDuration: 10000,
      });
    },
  });

  const deleteAssistant = async () => {
    try {
      if (assistantId) await remove({ assistantId });
    } catch {}
  };

  return (
    <Modal
      header='Delete Assistant'
      identifier={assistant?.name}
      open={Boolean(assistantId)}
      setOpen={setOpen as (val: boolean | string | null) => void}
      background={theme.telnyx.red}
      icon={<DeleteOutlined />}
      info={
        'This assistant and all its configured tools will be permanently removed from your Telnyx account. This action cannot be undone. Do you want to continue?'
      }
      actions={
        <Stack direction='row' spacing={1}>
          <Button small onClick={() => setOpen(null)} disabled={isDeleteLoading}>
            Back
          </Button>
          <LoadingButton loading={isDeleteLoading} small customColor='red' minWidth='136px' onClick={deleteAssistant}>
            Delete Assistant
          </LoadingButton>
        </Stack>
      }
    />
  );
};
